import React from "react";

/**
 * 1-page A4 Cover Letter for the Marisa Peer / More Than Enough
 * "AI-Powered Social Media & Content Systems Manager" application.
 *
 * Exports:
 *   - CoverSheet (named): the printable <article>, reused in /application
 *   - CoverLetter (default): standalone /cover-letter page with toolbar + main
 */

const CREAM = "#f4ede0";
const INK = "#1a1a1a";
const INK_SOFT = "#3a342b";
const BRONZE = "#8a6f47";
const RULE_SOFT = "rgba(26,26,26,0.10)";

const SERIF = '"Playfair Display", Georgia, "Times New Roman", serif';
const SANS = "Inter, -apple-system, BlinkMacSystemFont, sans-serif";

const Eyebrow = ({ children }) => (
    <p
        style={{
            fontFamily: SANS,
            color: BRONZE,
            fontSize: 9,
            letterSpacing: "0.36em",
            textTransform: "uppercase",
            fontWeight: 600,
            margin: 0,
        }}
    >
        {children}
    </p>
);

export const CoverSheet = () => (
    <article
        className="cover-page"
        data-testid="cover-letter-sheet"
        style={{
            width: 794,
            height: 1120,
            maxWidth: "100%",
            margin: "0 auto",
            backgroundColor: CREAM,
            color: INK,
            boxShadow: "0 24px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(26,26,26,0.06)",
            padding: "56px 84px 44px",
            boxSizing: "border-box",
            position: "relative",
            fontFamily: SANS,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
        }}
    >
        {/* Top header strip */}
        <header
            style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                paddingBottom: 18,
                borderBottom: `1px solid ${RULE_SOFT}`,
                marginBottom: 36,
            }}
        >
            <div>
                <h1
                    style={{
                        fontFamily: SERIF,
                        fontSize: 36,
                        fontWeight: 500,
                        color: INK,
                        margin: 0,
                        letterSpacing: "-0.02em",
                        lineHeight: 1,
                    }}
                >
                    Tatva K
                </h1>
                <p
                    style={{
                        fontFamily: SERIF,
                        fontStyle: "italic",
                        fontSize: 13,
                        color: BRONZE,
                        margin: "6px 0 0",
                    }}
                >
                    AI Systems Architect &middot; Audio-Visual Producer &middot;
                    Direct-Response Strategist
                </p>
            </div>
            <div style={{ textAlign: "right" }}>
                <Eyebrow>Dubai, UAE</Eyebrow>
                <p
                    style={{
                        fontFamily: SANS,
                        fontSize: 9.5,
                        color: INK_SOFT,
                        margin: "6px 0 0",
                        letterSpacing: "0.04em",
                        lineHeight: 1.55,
                    }}
                >
                    tatvak@theresetsanctuary.org
                    <br />
                    +971 50 676 9007
                    <br />
                    theresetsanctuary.org
                </p>
            </div>
        </header>

        {/* Addressee */}
        <div style={{ marginBottom: 18 }}>
            <Eyebrow>To</Eyebrow>
            <p
                style={{
                    fontFamily: SERIF,
                    fontSize: 16,
                    color: INK,
                    margin: "8px 0 0",
                    letterSpacing: "-0.005em",
                    lineHeight: 1.4,
                }}
            >
                Marisa Peer &amp; The More Than Enough Team
                <br />
                <span
                    style={{
                        fontFamily: SERIF,
                        fontStyle: "italic",
                        color: BRONZE,
                        fontSize: 13,
                    }}
                >
                    Re: AI-Powered Social Media &amp; Content Systems Manager
                </span>
            </p>
        </div>

        {/* Body */}
        <section
            style={{
                fontFamily: SERIF,
                fontSize: 12,
                lineHeight: 1.55,
                color: INK_SOFT,
                fontWeight: 400,
            }}
        >
            <p style={{ margin: "0 0 10px" }}>Dear Marisa &amp; team,</p>

            <p style={{ margin: "0 0 10px" }}>
                I&rsquo;m writing to apply for the AI-Powered Social Media &amp;
                Content Systems Manager role. The work More Than Enough is doing
                &mdash; meeting people where they are and helping them realise their
                own worth &mdash; is genuinely meaningful to me, and the chance to
                support that mission with the toolset I&rsquo;ve spent years building
                feels like a rare fit.
            </p>

            <p style={{ margin: "0 0 10px" }}>
                A short introduction. I&rsquo;m a Berklee College of Music alumnus, a
                TEDx speaker on sound and cognition, and an Insight Timer Verified
                Teacher with{" "}
                <strong style={{ color: INK }}>
                    4,400+ hours of live sound practice
                </strong>{" "}
                behind me. Over the last twenty years I&rsquo;ve scaled audio
                properties to{" "}
                <strong style={{ color: INK }}>
                    150M+ verified organic streams
                </strong>{" "}
                and grown a personal distribution footprint of{" "}
                <strong style={{ color: INK }}>385K+ YouTube subscribers</strong> and{" "}
                <strong style={{ color: INK }}>130K+ Instagram followers</strong>{" "}
                across my work as a producer, broadcaster and founder.
            </p>

            <p style={{ margin: "0 0 10px" }}>
                What I think I&rsquo;d bring to your team beyond the creative work is
                the systems side. For my own studio, The Reset Sanctuary in Dubai,
                I&rsquo;ve built and personally maintain a full content and marketing
                stack &mdash;{" "}
                <em style={{ color: BRONZE, fontStyle: "italic" }}>
                    FastAPI backend, Meta Conversions API, server-side Stripe
                    webhooks, automated MailerLite segmentation
                </em>{" "}
                &mdash; running at clean event-match quality and tied directly to
                revenue. I mention it only because the role talks about building
                systems rather than just managing channels, and that&rsquo;s the side
                of the work I love most.
            </p>

            <p style={{ margin: "0 0 10px" }}>
                On the creative side I work daily with the stack your team is leaning
                into: Claude and Emergent APIs for prompt logic, Kaiber AI for
                generative video, ElevenLabs for voice work, Final Cut Pro X for
                short-form editing, and the Meta Glasses Gen 2 ecosystem for
                multi-format capture. I run a daily content cadence, repurpose
                long-form podcasts and talks into short-form, and have been trained
                in the Andromeda framework under Jon Loomer&rsquo;s performance
                strategies. For copy I lean on Gary Halbert and Ben Settle
                direct-response frameworks &mdash; the kind that respects the reader
                and earns attention rather than chasing it.
            </p>

            <p style={{ margin: "0 0 10px" }}>
                I&rsquo;m drawn to this role because I care about the standard.
                Wellness content lives or dies on whether the audience can feel the
                integrity behind it, and I&rsquo;d be glad to bring that sensitivity
                to every piece that goes out under the More Than Enough name.
            </p>

            <p style={{ margin: "0 0 10px" }}>
                I&rsquo;m based in Dubai. My own projects are team-supported, so I
                can give this role real focus from day one.
            </p>

            <p style={{ margin: "0 0 10px" }}>
                One practical note: I run my own enterprise-grade AI infrastructure
                and pay for my private API access out of pocket, so I can hit the
                ground producing on day one without waiting on credential
                provisioning. From a single studio setup I&rsquo;m comfortably
                outputting at the volume of a small content team.
            </p>

            <p style={{ margin: "0 0 10px" }}>
                If it&rsquo;s helpful, everything I&rsquo;ve described is live at{" "}
                <em style={{ color: BRONZE, fontStyle: "italic" }}>
                    theresetsanctuary.org
                </em>{" "}
                and I&rsquo;d welcome the chance to walk you through it whenever
                suits the team.
            </p>

            <p style={{ margin: 0 }}>
                Thank you for reading &mdash; and for the work you do.
            </p>
        </section>

        {/* Sign off */}
        <div
            style={{
                marginTop: "auto",
                paddingTop: 20,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
            }}
        >
            <div>
                <p
                    style={{
                        fontFamily: SERIF,
                        fontStyle: "italic",
                        fontSize: 24,
                        color: INK,
                        margin: 0,
                        letterSpacing: "-0.01em",
                        lineHeight: 1,
                    }}
                >
                    With sound,
                </p>
                <p
                    style={{
                        fontFamily: SERIF,
                        fontSize: 20,
                        color: BRONZE,
                        margin: "8px 0 0",
                        fontWeight: 500,
                        letterSpacing: "-0.01em",
                    }}
                >
                    Tatva K
                </p>
            </div>
            <div style={{ textAlign: "right" }}>
                <Eyebrow>The Reset Sanctuary</Eyebrow>
                <p
                    style={{
                        fontFamily: SANS,
                        fontSize: 9.5,
                        color: INK_SOFT,
                        margin: "8px 0 0",
                        letterSpacing: "0.04em",
                        lineHeight: 1.6,
                    }}
                >
                    Dubai, UAE
                </p>
            </div>
        </div>
    </article>
);

const CoverLetter = () => {
    return (
        <main
            data-testid="cover-letter-page"
            style={{
                backgroundColor: "#2a2620",
                minHeight: "100vh",
                padding: "40px 16px 80px",
                fontFamily: SANS,
            }}
        >
            {/* Toolbar — hidden in print */}
            <div
                className="cover-toolbar"
                style={{
                    maxWidth: 794,
                    margin: "0 auto 24px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    flexWrap: "wrap",
                }}
            >
                <p
                    style={{
                        color: "#c9a227",
                        fontSize: 11,
                        letterSpacing: "0.3em",
                        textTransform: "uppercase",
                        margin: 0,
                        fontWeight: 600,
                    }}
                >
                    Tatva K &middot; Cover Letter &middot; Print → Save as PDF (A4)
                </p>
                <button
                    type="button"
                    onClick={() => window.print()}
                    data-testid="cover-letter-print-btn"
                    style={{
                        backgroundColor: "#c9a227",
                        color: "#1a1a1a",
                        border: "none",
                        padding: "10px 22px",
                        fontSize: 11,
                        letterSpacing: "0.22em",
                        textTransform: "uppercase",
                        fontWeight: 700,
                        cursor: "pointer",
                        borderRadius: 999,
                    }}
                >
                    Print / Save PDF
                </button>
            </div>

            <CoverSheet />

            <style>{`
                @page { size: A4; margin: 0; }
                @media print {
                    body, html { background: #ffffff !important; }
                    main[data-testid="cover-letter-page"] { background: #ffffff !important; padding: 0 !important; }
                    .cover-toolbar { display: none !important; }
                    .cover-page {
                        box-shadow: none !important;
                        margin: 0 auto !important;
                    }
                    nav, footer[data-testid="site-footer"],
                    [data-testid="floating-whatsapp-btn"],
                    [data-testid="cookie-consent-banner"] { display: none !important; }
                }
            `}</style>
        </main>
    );
};

export default CoverLetter;
